import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import AdminRichTextEditor, { AdminRichTextEditorHandle } from './AdminRichTextEditor'
import { CourseHomework, HomeworkApi } from '../../services/homeworkApi'
import apiClient from '../../services/apiClient'
import HomeworkSkillAttach from '../homework/HomeworkSkillAttach'

type Props = { courseId: number }

type Classroom = { id: number; name: string; code?: string | null; startDate?: string | null }

export default function AdminCourseHomeworkManager({ courseId }: Props) {
  const [items, setItems] = useState<CourseHomework[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<CourseHomework | null>(null)
  const [creating, setCreating] = useState(false)
  const [classrooms, setClassrooms] = useState<Classroom[]>([])
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<number[]>([])
  const [form, setForm] = useState({
    name: '',
    contentHtml: '',
    templateHtml: '',
    homeworkType: 'submission' as 'submission' | 'self_practice',
    allowCompletionNote: true,
    isActive: true,
    aiAutoScanEnabled: false,
    aiRequireAdminApprove: true,
    aiCanMarkComplete: false,
    aiScanCooldownMinutes: 15,
  })
  const [saving, setSaving] = useState(false)
  const contentEditorRef = useRef<AdminRichTextEditorHandle | null>(null)
  const templateEditorRef = useRef<AdminRichTextEditorHandle | null>(null)

  // Gán lớp từ danh sách (không cần vào edit)
  const [assignHw, setAssignHw] = useState<CourseHomework | null>(null)
  const [assignSelectedIds, setAssignSelectedIds] = useState<number[]>([])
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignSaving, setAssignSaving] = useState(false)

  const load = async () => {
    try {
      setLoading(true)
      const data = await HomeworkApi.listByCourse(courseId)
      setItems(data)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được BTVN')
    } finally {
      setLoading(false)
    }
  }

  const loadClassrooms = async () => {
    try {
      const res = await apiClient.get(`/admin/courses/${courseId}/classrooms`)
      const list = res.data?.data || res.data || []
      const arr: Classroom[] = Array.isArray(list) ? list : list.items || []
      // startDate giảm dần (API đã ORDER BY start_date DESC; sort lại để chắc)
      arr.sort((a: any, b: any) => {
        const ta = a.startDate ? new Date(a.startDate).getTime() : 0
        const tb = b.startDate ? new Date(b.startDate).getTime() : 0
        return tb - ta
      })
      setClassrooms(arr)
    } catch {
      setClassrooms([])
    }
  }

  useEffect(() => {
    void load()
    void loadClassrooms()
  }, [courseId])

  const openCreate = () => {
    setCreating(true)
    setEditing(null)
    setSelectedClassroomIds([])
    setForm({
      name: '',
      contentHtml: '',
      templateHtml: '',
      homeworkType: 'submission',
      allowCompletionNote: true,
      isActive: true,
      aiAutoScanEnabled: false,
      aiRequireAdminApprove: true,
      aiCanMarkComplete: false,
      aiScanCooldownMinutes: 15,
    })
  }

  const openEdit = async (hw: CourseHomework) => {
    try {
      const full = await HomeworkApi.get(hw.id)
      setEditing(full)
      setCreating(false)
      setForm({
        name: full.name,
        contentHtml: full.contentHtml,
        templateHtml: full.templateHtml || '',
        homeworkType: full.homeworkType === 'self_practice' ? 'self_practice' : 'submission',
        allowCompletionNote: Boolean(full.allowCompletionNote ?? 1),
        isActive: Boolean(full.isActive),
        aiAutoScanEnabled: Boolean(full.aiAutoScanEnabled),
        aiRequireAdminApprove: Boolean(full.aiRequireAdminApprove),
        aiCanMarkComplete: Boolean(full.aiCanMarkComplete),
        aiScanCooldownMinutes: Number(full.aiScanCooldownMinutes || 15),
      })
      setSelectedClassroomIds((full.classrooms || []).map((c) => Number(c.classroomId)))
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được BTVN')
    }
  }

  const save = async () => {
    const liveContent = contentEditorRef.current?.getHtml?.() || form.contentHtml
    const liveTemplate = templateEditorRef.current?.getHtml?.() ?? form.templateHtml
    const payload = {
      ...form,
      contentHtml: liveContent,
      templateHtml: liveTemplate,
    }
    if (!payload.name.trim() || !payload.contentHtml.trim()) {
      toast.error('Nhập tên và nội dung BTVN')
      return
    }
    setForm((f) => ({ ...f, contentHtml: liveContent, templateHtml: liveTemplate }))
    try {
      setSaving(true)
      let id = editing?.id
      if (creating) {
        const created = await HomeworkApi.create(courseId, payload)
        id = created.id
        toast.success('Đã tạo BTVN')
      } else if (editing) {
        await HomeworkApi.update(editing.id, payload)
        toast.success('Đã cập nhật BTVN')
      }
      if (id) {
        await HomeworkApi.assignClassrooms(id, selectedClassroomIds)
      }
      setCreating(false)
      setEditing(null)
      await load()
    } catch (e: any) {
      toast.error(e?.message || 'Lưu thất bại')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: number) => {
    if (!confirm('Xóa BTVN này?')) return
    try {
      await HomeworkApi.remove(id)
      toast.success('Đã xóa')
      await load()
    } catch (e: any) {
      toast.error(e?.message || 'Xóa thất bại')
    }
  }

  const openAssignClassrooms = async (hw: CourseHomework) => {
    setAssignHw(hw)
    setAssignSelectedIds([])
    setAssignLoading(true)
    try {
      if (classrooms.length === 0) await loadClassrooms()
      const full = await HomeworkApi.get(hw.id)
      setAssignSelectedIds((full.classrooms || []).map((c) => Number(c.classroomId)))
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được lớp đã gán')
      setAssignHw(null)
    } finally {
      setAssignLoading(false)
    }
  }

  const saveAssignClassrooms = async () => {
    if (!assignHw) return
    try {
      setAssignSaving(true)
      await HomeworkApi.assignClassrooms(assignHw.id, assignSelectedIds)
      toast.success('Đã gán lớp cho BTVN')
      setAssignHw(null)
      await load()
    } catch (e: any) {
      toast.error(e?.message || 'Gán lớp thất bại')
    } finally {
      setAssignSaving(false)
    }
  }

  const showForm = creating || editing

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4 shrink-0">
        <h3 className="text-lg font-bold text-m2-on-surface">Quản lý BTVN</h3>
        {!showForm && (
          <button
            type="button"
            onClick={openCreate}
            className="bg-m2-primary/10 text-m2-primary border border-m2-primary/30 px-4 py-2 rounded-md flex items-center text-sm font-medium"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" /> Thêm BTVN
          </button>
        )}
      </div>

      {showForm ? (
        <div className="flex-1 overflow-auto space-y-4 pb-8">
          <div>
            <label className="block text-sm font-medium mb-1">Tên bài</label>
            <input
              className="w-full border border-m2-outline-variant rounded-md px-3 py-2 text-sm"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Loại BTVN</label>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="homeworkType"
                  checked={form.homeworkType === 'submission'}
                  onChange={() => setForm((f) => ({ ...f, homeworkType: 'submission' }))}
                />
                Nộp bài
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="homeworkType"
                  checked={form.homeworkType === 'self_practice'}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      homeworkType: 'self_practice',
                      aiAutoScanEnabled: false,
                      aiCanMarkComplete: false,
                    }))
                  }
                />
                Tự thực hành
              </label>
            </div>
            <p className="text-xs text-m2-on-surface-variant mt-1">
              {form.homeworkType === 'self_practice'
                ? 'Học viên tự đánh dấu hoàn thành, không nộp bài HTML.'
                : 'Học viên viết/nộp bài để admin (hoặc AI) chấm.'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nội dung đề (HTML)</label>
            <AdminRichTextEditor
              ref={contentEditorRef}
              value={form.contentHtml}
              onChange={(v) => setForm((f) => ({ ...f, contentHtml: v }))}
            />
          </div>
          {form.homeworkType === 'submission' && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Template bài làm (HTML)
              </label>
              <p className="text-xs text-m2-on-surface-variant mb-2">
                Học viên mở làm bài sẽ được prefill template này (heading/cấu trúc sẵn) để viết theo.
              </p>
              <AdminRichTextEditor
                ref={templateEditorRef}
                value={form.templateHtml}
                onChange={(v) => setForm((f) => ({ ...f, templateHtml: v }))}
              />
            </div>
          )}
          {form.homeworkType === 'self_practice' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allowCompletionNote}
                onChange={(e) =>
                  setForm((f) => ({ ...f, allowCompletionNote: e.target.checked }))
                }
              />
              Cho phép nhập ghi chú khi hoàn thành
            </label>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">
              Gán lớp học{' '}
              <span className="font-normal text-m2-on-surface-variant">
                (theo ngày bắt đầu, mới → cũ)
              </span>
            </label>
            <div className="flex flex-col gap-2 max-h-64 overflow-auto border border-m2-outline-variant rounded-lg p-2">
              {classrooms.map((c) => {
                const checked = selectedClassroomIds.includes(c.id)
                return (
                  <label
                    key={c.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-sm cursor-pointer ${
                      checked
                        ? 'bg-m2-primary/15 border-m2-primary text-m2-primary'
                        : 'border-m2-outline-variant hover:bg-m2-surface-low'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedClassroomIds((prev) =>
                            checked ? prev.filter((id) => id !== c.id) : [...prev, c.id]
                          )
                        }
                      />
                      <span className="truncate font-medium">{c.name}</span>
                    </span>
                    <span className="text-xs text-m2-on-surface-variant shrink-0">
                      {c.startDate
                        ? new Date(c.startDate).toLocaleDateString('vi-VN')
                        : '—'}
                    </span>
                  </label>
                )
              })}
              {classrooms.length === 0 && (
                <p className="text-sm text-m2-on-surface-variant px-2 py-1">Chưa có lớp học</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              Đang hoạt động
            </label>
            {form.homeworkType === 'submission' && (
              <>
                {(
                  [
                    ['aiAutoScanEnabled', 'Bật AI quét chấm tự động'],
                    ['aiRequireAdminApprove', 'AI comment cần admin duyệt'],
                    ['aiCanMarkComplete', 'Cho AI quyết định hoàn thành'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(form[key])}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                    />
                    {label}
                  </label>
                ))}
                <label className="flex items-center gap-2">
                  Cooldown AI (phút)
                  <input
                    type="number"
                    min={1}
                    className="w-20 border rounded px-2 py-1"
                    value={form.aiScanCooldownMinutes}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, aiScanCooldownMinutes: Number(e.target.value) || 15 }))
                    }
                  />
                </label>
              </>
            )}
          </div>

          {editing && (
            <HomeworkSkillAttach homeworkId={editing.id} />
          )}

          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="px-4 py-2 rounded-md bg-m2-primary text-white text-sm"
            >
              {saving ? 'Đang lưu...' : 'Lưu'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false)
                setEditing(null)
              }}
              className="px-4 py-2 rounded-md border text-sm"
            >
              Hủy
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center text-m2-on-surface-variant">
          Đang tải...
        </div>
      ) : items.length === 0 ? (
        <div className="flex-1 border rounded-xl p-12 flex flex-col items-center justify-center text-m2-on-surface-variant">
          <p>Chưa có BTVN nào.</p>
          <button type="button" onClick={openCreate} className="mt-4 text-m2-primary underline">
            Tạo BTVN đầu tiên
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-auto space-y-3">
          {items.map((hw) => (
            <div
              key={hw.id}
              className="border border-m2-outline-variant rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
            >
              <div>
                <p className="font-semibold text-m2-on-surface">
                  {hw.name}{' '}
                  <span
                    className={`ml-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${
                      hw.homeworkType === 'self_practice'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-sky-50 text-sky-700'
                    }`}
                  >
                    {hw.homeworkType === 'self_practice' ? 'Tự thực hành' : 'Nộp bài'}
                  </span>
                </p>
                <p className="text-xs text-m2-on-surface-variant mt-1">
                  {hw.classroomCount || 0} lớp · {hw.submissionCount || 0} bài nộp
                  {hw.aiAutoScanEnabled ? ' · AI scan ON' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/admin/courses/${courseId}/homeworks/${hw.id}/submissions`}
                  className="px-3 py-1.5 text-sm rounded-md border border-m2-primary/30 text-m2-primary"
                >
                  Bài làm
                </Link>
                <button
                  type="button"
                  onClick={() => void openAssignClassrooms(hw)}
                  title="Gán lớp"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm"
                >
                  📝
                </button>
                <button
                  type="button"
                  onClick={() => void openEdit(hw)}
                  title="Chỉnh sửa"
                  className="p-2 rounded-md hover:bg-m2-surface-high"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => void remove(hw.id)}
                  title="Xóa"
                  className="p-2 rounded-md hover:bg-red-50 text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {assignHw && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-m2-surface-container rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-m2-outline-variant">
              <div>
                <h3 className="text-lg font-bold text-m2-on-surface">Gán lớp cho BTVN</h3>
                <p className="text-xs text-m2-on-surface-variant mt-0.5">{assignHw.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setAssignHw(null)}
                className="text-sm text-m2-on-surface-variant hover:text-m2-on-surface px-3 py-1.5 rounded-lg hover:bg-m2-surface-high"
              >
                Đóng
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <p className="text-xs text-m2-on-surface-variant mb-3">
                Lớp theo ngày bắt đầu, mới → cũ
              </p>
              {assignLoading ? (
                <p className="text-sm text-m2-on-surface-variant py-6 text-center">Đang tải…</p>
              ) : classrooms.length === 0 ? (
                <p className="text-sm text-m2-on-surface-variant py-6 text-center">Chưa có lớp học</p>
              ) : (
                <ul className="space-y-2">
                  {classrooms.map((c) => {
                    const checked = assignSelectedIds.includes(c.id)
                    return (
                      <li key={c.id}>
                        <label
                          className={`flex items-center justify-between gap-3 p-3 rounded-xl border cursor-pointer ${
                            checked
                              ? 'bg-m2-primary/15 border-m2-primary'
                              : 'border-m2-outline-variant hover:bg-m2-surface-high'
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setAssignSelectedIds((prev) =>
                                  checked ? prev.filter((id) => id !== c.id) : [...prev, c.id]
                                )
                              }
                            />
                            <span className="text-sm font-medium truncate">{c.name}</span>
                          </span>
                          <span className="text-xs text-m2-on-surface-variant shrink-0">
                            {c.startDate
                              ? new Date(c.startDate).toLocaleDateString('vi-VN')
                              : '—'}
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-m2-outline-variant">
              <button
                type="button"
                onClick={() => setAssignHw(null)}
                className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant text-m2-on-surface-variant hover:bg-m2-surface-high"
              >
                Hủy
              </button>
              <button
                type="button"
                disabled={assignSaving || assignLoading}
                onClick={() => void saveAssignClassrooms()}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {assignSaving ? 'Đang lưu…' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
